import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SESSION_STORAGE_KEY = 'evidence_quote_source';

// Canonical option values for "Where did you hear about us?" (must match form dropdown)
const SOURCE_OPTIONS = ['Yelp', 'Google', 'Instagram', 'Facebook', 'TikTok', 'Friends/Family', 'Other'];

function normalizeSource(value: string): string {
  const lower = value.trim().toLowerCase();
  const match = SOURCE_OPTIONS.find((opt) => opt.toLowerCase() === lower);
  return match ?? value.trim();
}

// Create a context for the source parameter with a default value of "default"
const SourceContext = createContext<string>('default');
const SourceUpdateContext = createContext<React.Dispatch<React.SetStateAction<string>>>(() => {});

export function useSource() {
  return useContext(SourceContext);
}

export function useUpdateSource() {
  return useContext(SourceUpdateContext);
}

export function SourceProvider({ children }: { children: React.ReactNode }) {
  const [source, setSource] = useState<string>(() => {
    if (typeof window === 'undefined') return 'default';
    return sessionStorage.getItem(SESSION_STORAGE_KEY) || 'default';
  });

  return (
    <SourceContext.Provider value={source}>
      <SourceUpdateContext.Provider value={setSource}>
        {children}
      </SourceUpdateContext.Provider>
    </SourceContext.Provider>
  );
}

export function SourceWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const setSource = useUpdateSource();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlSource = searchParams.get('source');
    if (urlSource) {
      const normalized = normalizeSource(urlSource);
      setSource(normalized);
      sessionStorage.setItem(SESSION_STORAGE_KEY, normalized);
    }
  }, [location, setSource]);

  return <>{children}</>;
}
